import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Building2, 
  MapPin, 
  Shield, 
  TrendingUp, 
  Truck, 
  Award, 
  Users, 
  DollarSign, 
  CheckCircle, 
  Globe, 
  Package,
  Leaf,
  ChevronRight,
  Send,
  Phone,
  Mail,
  Store
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Wholesale = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    website: '',
    business_type: '',
    location: '',
    estimated_volume: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.company_name || !formData.contact_name || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/capture`, {
        name: formData.contact_name,
        email: formData.email,
        phone: formData.phone,
        lead_type: 'wholesale',
        source: 'wholesale_page',
        product_name: 'Wholesale Inquiry',
        notes: `Company: ${formData.company_name}\nWebsite: ${formData.website}\nBusiness Type: ${formData.business_type}\nLocation: ${formData.location}\nEstimated Volume: ${formData.estimated_volume}\nMessage: ${formData.message}`
      });
      setSubmitted(true);
      toast.success('Thank you! Our wholesale team will contact you within 24-48 hours.');
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: MapPin,
      title: 'Proudly Canadian',
      description: 'Products designed in Canada, assembled in Canada, and tested in Canada. Strengthen North American supply chains with a trusted local partner.'
    },
    {
      icon: Shield,
      title: 'Premium Quality & Warranty',
      description: 'Industry-leading build quality backed by comprehensive warranty support. We stand behind every unit we ship.'
    },
    {
      icon: Truck,
      title: 'No Import Duties for Canada',
      description: 'Canadian retailers avoid costly import duties and tariffs. Faster shipping, lower costs, happier customers.'
    },
    {
      icon: TrendingUp,
      title: 'Growing Market Demand',
      description: 'Freeze drying is one of the fastest-growing home appliance categories. Position your business for success.'
    },
    {
      icon: DollarSign,
      title: 'Competitive Margins',
      description: 'Wholesale pricing designed to give you healthy margins while offering competitive retail prices.'
    },
    {
      icon: Users,
      title: 'Marketing & Sales Support',
      description: 'Access to marketing materials, product training, and dedicated wholesale account management.'
    }
  ];

  const whyCanadian = [
    {
      icon: Globe,
      title: 'USMCA/CUSMA Compliant',
      description: 'Full compliance with United States-Mexico-Canada Agreement trade regulations for seamless cross border commerce.'
    },
    {
      icon: Leaf,
      title: 'Environmentally Conscious',
      description: 'Reduced carbon footprint from shorter shipping distances. Sustainable manufacturing practices.'
    },
    {
      icon: Award,
      title: 'North American Standards',
      description: 'Built to meet or exceed all North American electrical and safety certifications (CSA, UL, ETL).'
    },
    {
      icon: Store,
      title: 'Local Service Network',
      description: 'Faster warranty service and parts availability. No overseas shipping delays for rebuilds or replacements.'
    }
  ];

  const businessTypes = [
    'Retail Store (Brick & Mortar)',
    'Online Retailer / E-commerce',
    'Kitchen & Home Appliance Store',
    'Outdoor & Camping Retailer',
    'Farm & Agricultural Supply',
    'Preparedness & Emergency Supply',
    'Food Processing / Commercial',
    'Distributor / Wholesaler',
    'Other'
  ];

  const volumeOptions = [
    '5-10 units/month',
    '10-25 units/month',
    '25-50 units/month',
    '50-100 units/month',
    '100+ units/month',
    'Not sure yet'
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      {/* Hero Section */}
      <div className="pt-36 pb-20 bg-gradient-to-br from-[#003456] to-[#002B47] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 text-white/70 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span>Wholesale Purchasing</span>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                  <Building2 className="h-6 w-6" />
                </div>
                <span className="text-sm font-medium text-white/80 uppercase tracking-wider">Wholesale Program</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Partner With Canada's Premier Freeze Dryer Manufacturer
              </h1>
              <p className="text-lg text-white/80 mb-8 leading-relaxed">
                Bring D Freeze freeze dryers to your customers. As a Canadian manufacturer, we offer Canadian retailers 
                and e commerce merchants unique advantages including no import duties, faster shipping, 
                and comprehensive support.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#inquiry-form">
                  <Button size="lg" className="bg-[#CE8946] hover:bg-[#e5b01a] text-[#0a0203] font-semibold">
                    Become a Wholesale Partner
                  </Button>
                </a>
                <a href="#benefits">
                  <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10">
                    Learn More
                  </Button>
                </a>
              </div>
            </div>
            
            <div className="hidden lg:block">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#CE8946]/20 to-transparent rounded-3xl transform rotate-3"></div>
                <div className="relative bg-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/10">
                  <div className="grid grid-cols-2 gap-6 text-center">
                    <div className="p-4">
                      <p className="text-3xl font-bold text-[#CE8946]">Canadian</p>
                      <p className="text-sm text-white/70 mt-1">Designed & Assembled</p>
                    </div>
                    <div className="p-4">
                      <p className="text-4xl font-bold text-[#CE8946]">$0</p>
                      <p className="text-sm text-white/70 mt-1">Import Duties (CAN)</p>
                    </div>
                    <div className="p-4">
                      <p className="text-3xl font-bold text-[#CE8946]">Tested</p>
                      <p className="text-sm text-white/70 mt-1">In Canada</p>
                    </div>
                    <div className="p-4">
                      <p className="text-4xl font-bold text-[#CE8946]">Fast</p>
                      <p className="text-sm text-white/70 mt-1">Canadian Shipping</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div id="benefits" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#003456] uppercase tracking-wider">Why Partner With Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mt-3 mb-4">
              Benefits of Carrying D Freeze Products
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Join a growing network of Canadian retailers who trust D Freeze for quality, reliability, and profitability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="bg-[#F8FAFC] rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-[#003456] rounded-xl flex items-center justify-center mb-4">
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0a0203] mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Why Canadian Brand Section */}
      <div className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-medium text-[#003456] uppercase tracking-wider">Canadian Advantage</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mt-3 mb-6">
                Why Choose a Canadian Freeze Dryer Brand?
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                In today's uncertain global trade environment, sourcing from a Canadian manufacturer 
                provides stability, reliability, and competitive advantages for Canadian retailers.
              </p>
              
              <div className="space-y-6">
                {whyCanadian.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4">
                      <div className="w-12 h-12 bg-[#dbeef5] rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="h-6 w-6 text-[#003456]" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0a0203] mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 shadow-sm">
              <h3 className="text-xl font-bold text-[#0a0203] mb-6 flex items-center gap-2">
                <Package className="h-6 w-6 text-[#003456]" />
                Current Trade Realities
              </h3>
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="font-medium text-amber-800 mb-1">Import Tariff Concerns</p>
                  <p className="text-sm text-amber-700">
                    With ongoing trade tensions, import duties on overseas products can change rapidly. 
                    Canadian sourced products provide pricing stability.
                  </p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <p className="font-medium text-green-800 mb-1">Supply Chain Reliability</p>
                  <p className="text-sm text-green-700">
                    Domestic manufacturing means no overseas shipping delays, port congestion issues, 
                    or container shortages affecting your inventory.
                  </p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="font-medium text-blue-800 mb-1">Customer Preference</p>
                  <p className="text-sm text-blue-700">
                    More consumers are actively seeking locally-made products. "Assembled in Canada" 
                    is a selling point that resonates with buyers.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inquiry Form Section */}
      <div id="inquiry-form" className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-sm font-medium text-[#003456] uppercase tracking-wider">Get Started</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mt-3 mb-4">
              Wholesale Inquiry Form
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Interested in becoming a wholesale partner? Fill out the form below and our team will 
              contact you within 24-48 business hours to discuss partnership opportunities.
            </p>
          </div>

          {submitted ? (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-3">Thank You for Your Interest!</h3>
              <p className="text-green-700 mb-6">
                Our wholesale team has received your inquiry and will contact you within 24-48 business hours 
                to discuss partnership opportunities.
              </p>
              <p className="text-sm text-green-600">
                In the meantime, feel free to explore our <Link to="/products" className="underline font-medium">product catalog</Link> or 
                call us at <a href="tel:1-888-223-1435" className="underline font-medium">1-888-223-1435</a>.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-[#F8FAFC] rounded-2xl p-8 space-y-6" data-testid="wholesale-form">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="Your company name"
                    required
                    data-testid="company-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Your full name"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="business@company.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Website
                </label>
                <Input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://www.yourcompany.com"
                  data-testid="website-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Type
                  </label>
                  <select
                    value={formData.business_type}
                    onChange={(e) => setFormData({ ...formData, business_type: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-[#003456]"
                    data-testid="business-type-select"
                  >
                    <option value="">Select your business type</option>
                    {businessTypes.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location (City, State/Province)
                  </label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="e.g., Calgary, Alberta"
                    data-testid="location-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Monthly Volume
                </label>
                <select
                  value={formData.estimated_volume}
                  onChange={(e) => setFormData({ ...formData, estimated_volume: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-[#003456]"
                  data-testid="volume-select"
                >
                  <option value="">Select estimated volume</option>
                  {volumeOptions.map((volume) => (
                    <option key={volume} value={volume}>{volume}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003456] focus:border-[#003456] resize-none"
                  placeholder="Tell us about your business, your customers, and what you're looking for in a freeze dryer partner..."
                  data-testid="message-textarea"
                />
              </div>

              <div className="bg-gray-100 rounded-xl p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-2">What happens next?</p>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Our wholesale team will review your application</li>
                  <li>We'll reach out within 24-48 business hours</li>
                  <li>We'll discuss pricing, minimums, and partnership terms</li>
                  <li>Access wholesale portal and marketing materials upon approval</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#003456] hover:bg-[#002B47] text-white py-6 text-lg font-semibold"
                data-testid="submit-wholesale-btn"
              >
                {submitting ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                ) : (
                  <>
                    <Send className="mr-2 h-5 w-5" /> Submit Wholesale Inquiry
                  </>
                )}
              </Button>

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to be contacted by our wholesale team. 
                Your information will be kept confidential and used solely for partnership discussions.
              </p>
            </form>
          )}
        </div>
      </div>

      {/* Contact CTA */}
      <div className="py-16 bg-[#003456]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Have Questions? We're Here to Help
          </h3>
          <p className="text-white/80 mb-8">
            Our wholesale team is ready to discuss partnership opportunities and answer any questions.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a 
              href="tel:1-888-223-1435" 
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl transition-colors"
            >
              <Phone className="h-5 w-5" />
              1-888-223-1435
            </a>
            <a 
              href="mailto:wholesale@homelyph.com" 
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-xl transition-colors"
            >
              <Mail className="h-5 w-5" />
              wholesale@homelyph.com
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Wholesale;
